// src/components/Footer.jsx
import { useEffect, useState } from 'react'
import { settingsAPI } from '../services/api'

const Footer = () => {
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    fetchSettings()
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const fetchSettings = async () => {
    try {
      const settingsData = await settingsAPI.getSettings()
      setSettings(settingsData)
    } catch (err) {
      console.error('Error fetching settings:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <footer className="bg-gradient-to-br from-cyan-900 via-cyan-800 to-blue-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse delay-150"></div>
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse delay-300"></div>
            </div>
            <p className="text-cyan-200 text-sm">Loading footer content...</p>
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer className="bg-gradient-to-br from-cyan-900 via-cyan-800 to-blue-900 text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
      <div className="absolute top-0 left-0 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 py-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          
          {/* Clinic Info Column */}
          <div className="space-y-6">
            <div className="transition-all duration-500 delay-100 hover:transform hover:scale-105">
              <h3 className="text-3xl font-black mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
                {settings.clinic_name || "Emmanuel Total DentalCare"}
              </h3>
              <p className="text-cyan-100 leading-relaxed mb-6">
                {settings.about_summary || "Providing quality dental care with compassion and expertise since 2005."}
                
              </p>
            </div>
            
            {/* Enhanced Social Links */}
            <div className="flex space-x-4">
              {settings.facebook_url && (
                <a 
                  href={settings.facebook_url} 
                  className="group bg-cyan-800/50 hover:bg-cyan-700 w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 shadow-lg hover:shadow-cyan-500/25 backdrop-blur-sm border border-cyan-700/50"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">Facebook</span>
                  <i className="fab fa-facebook text-xl group-hover:text-white transition-colors"></i>
                </a>
              )}
              {settings.twitter_url && (
                <a 
                  href={settings.twitter_url} 
                  className="group bg-cyan-800/50 hover:bg-cyan-700 w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 shadow-lg hover:shadow-cyan-500/25 backdrop-blur-sm border border-cyan-700/50"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">Twitter</span>
                  <i className="fab fa-twitter text-xl group-hover:text-white transition-colors"></i>
                </a>
              )}
              {settings.instagram_url && (
                <a 
                  href={settings.instagram_url} 
                  className="group bg-cyan-800/50 hover:bg-cyan-700 w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 shadow-lg hover:shadow-cyan-500/25 backdrop-blur-sm border border-cyan-700/50"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">Instagram</span>
                  <i className="fab fa-instagram text-xl group-hover:text-white transition-colors"></i>
                </a>
              )}
              {settings.linkedin_url && (
                <a 
                  href={settings.linkedin_url} 
                  className="group bg-cyan-800/50 hover:bg-cyan-700 w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 shadow-lg hover:shadow-cyan-500/25 backdrop-blur-sm border border-cyan-700/50"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">LinkedIn</span>
                  <i className="fab fa-linkedin text-xl group-hover:text-white transition-colors"></i>
                </a>
              )}
            </div>
          </div>
          
          {/* Quick Links Column */}
          <div className="transition-all duration-500 delay-200">
            <h4 className="text-xl font-bold mb-6 text-white flex items-center">
              <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 animate-pulse"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { href: "/", label: "Home", icon: "🏠" },
                { href: "/about", label: "About Us", icon: "👥" },
                { href: "/services", label: "Services", icon: "🦷" },
                { href: "/appointment", label: "Appointment", icon: "📅" }
              ].map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    className="group flex items-center space-x-3 text-cyan-100 hover:text-white transition-all duration-300 transform hover:translate-x-2"
                  >
                    <span className="text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:scale-110">
                      {link.icon}
                    </span>
                    <span className="font-medium group-hover:font-semibold">{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Services Column */}
          <div className="transition-all duration-500 delay-300">
            <h4 className="text-xl font-bold mb-6 text-white flex items-center">
              <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 animate-pulse delay-150"></span>
              Our Services
            </h4>
            <ul className="space-y-3">
              {[
                { href: "/services#cleaning", label: "Teeth Cleaning", icon: "✨" },
                { href: "/services#whitening", label: "Teeth Whitening", icon: "💎" },
                { href: "/services#implants", label: "Dental Implants", icon: "🦷" },
                { href: "/services#orthodontics", label: "Orthodontics", icon: "😁" }
              ].map((service, index) => (
                <li key={index}>
                  <a 
                    href={service.href} 
                    className="group flex items-center space-x-3 text-cyan-100 hover:text-white transition-all duration-300 transform hover:translate-x-2"
                  >
                    <span className="text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:scale-110">
                      {service.icon}
                    </span>
                    <span className="font-medium group-hover:font-semibold">{service.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info Column */}
          <div className="transition-all duration-500 delay-400">
            <h4 className="text-xl font-bold mb-6 text-white flex items-center">
              <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 animate-pulse delay-300"></span>
              Contact Info
            </h4>
            <address className="not-italic space-y-4">
              <div className="flex items-start space-x-3 group">
                <span className="text-cyan-300 text-lg mt-1 transform group-hover:scale-110 transition-transform duration-300">📍</span>
                <div>
                  <p className="text-cyan-100 group-hover:text-white transition-colors duration-300 font-medium">
                    {settings.clinic_address || ""}
                  </p>
                  <p className="text-cyan-100/80 text-sm group-hover:text-cyan-200 transition-colors duration-300">
                    {settings.clinic_city_state_zip || ""}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 group">
                <span className="text-cyan-300 text-lg transform group-hover:scale-110 transition-transform duration-300">📞</span>
                <a 
                  href={`tel:${settings.clinic_phone}`}
                  className="text-cyan-100 hover:text-white transition-colors duration-300 font-medium group-hover:font-semibold"
                >
                  {settings.clinic_phone || ""}
                </a>
              </div>
              
              <div className="flex items-center space-x-3 group">
                <span className="text-cyan-300 text-lg transform group-hover:scale-110 transition-transform duration-300">✉️</span>
                <a 
                  href={`mailto:${settings.clinic_email}`}
                  className="text-cyan-100 hover:text-white transition-colors duration-300 font-medium group-hover:font-semibold break-all"
                >
                  {settings.clinic_email || "info@emmanueldental.com"}
                </a>
              </div>
              
              {settings.emergency_phone && (
                <div className="flex items-center space-x-3 group bg-red-500/20 rounded-xl p-3 border border-red-500/30">
                  <span className="text-red-300 text-lg transform group-hover:scale-110 transition-transform duration-300">🚑</span>
                  <a 
                    href={`tel:${settings.emergency_phone}`}
                    className="text-red-100 hover:text-white transition-colors duration-300 font-bold group-hover:font-black"
                  >
                    {settings.emergency_phone}
                  </a>
                </div>
              )}
            </address>
          </div>
        </div>
        
        {/* Enhanced Bottom Bar */}
        <div className={`border-t border-cyan-700/50 py-8 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-cyan-200 font-medium">
                &copy; {new Date().getFullYear()} {settings.clinic_name || "Emmanuel DentalCare"}. All rights reserved.
              </p>
              <p className="text-cyan-300/70 text-sm mt-2">
                Committed to your dental health and beautiful smile.
              </p>
            </div>
            
            <div className="flex items-center space-x-3 text-cyan-200 group">
              <span className="text-sm font-medium">Developed by</span>
              <a 
                href="https://kwantabit.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-bold hover:text-white transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 bg-cyan-800/50 hover:bg-cyan-700 px-4 py-2 rounded-xl backdrop-blur-sm border border-cyan-700/50"
              >
                <span>KwantaBit Technologies</span>
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer